import { TextField } from '@client/new/components/basic';
import { ColorPicker } from './ColorPicker';
import { LandingMediaDropbox } from './MediaDropBox';
import { FieldTitle } from './RedStarTitle';
import { faImage, faVideo } from '@fortawesome/pro-regular-svg-icons';
import { useFormContext, Controller } from 'react-hook-form';
import { FieldDesc } from './common';
import CTAFormComponent from './CtaForm';
import { TwoColumns } from '@client/new/pages/LandingPagesBuilder/layouts';
import { IntroductionSectionSchema } from './OTPLandingPage.schema';

type Props = {
  imageUpload: (file: File) => Promise<string>;
  videoUpload: (file: File) => Promise<string>;
  index: number;
  value: IntroductionSectionSchema;
};
export function IntroductionForm(p: Props) {
  const MAX_INTRO_LENGTH = 500;
  const { register, control } = useFormContext();
  const toFormName = <T extends keyof IntroductionSectionSchema>(v: T) =>
    `sections.${p.index}.${v}`;
  const r = (s: keyof IntroductionSectionSchema) => register(toFormName(s));

  const firstCol = (
    <div className="max-w-[680px]">
      <div>
        <FieldTitle title={'Headline text'} />
        <FieldDesc>
          The headline is used to communicate the creator’s value proposition in
          the hero section of the sales page.
        </FieldDesc>
        <TextField
          {...r(`title`)}
          className="mb-[5px] max-w-[680px] w-full"
          inputProps={{
            placeholder: 'Headline text',
            maxLength: 60,
          }}
        />
      </div>

      <div className="mt-8">
        <FieldTitle title={'Subtitle'} />
        <FieldDesc>Some slogan</FieldDesc>
        <TextField
          {...r(`subtitle`)}
          className="mb-[5px] max-w-[680px] w-full"
          inputProps={{
            placeholder: 'Reach some goal',
            maxLength: 60,
          }}
        />
      </div>

      <div className="mt-8">
        <FieldTitle title={'Intro text'} />
        <FieldDesc>
          Short, captivating message that sells the program (new lines are
          preserved, max {MAX_INTRO_LENGTH} characters)
        </FieldDesc>
        <TextField
          {...r(`desc`)}
          className="mb-[5px] max-w-[680px] w-full"
          inputProps={{
            placeholder: 'Description',
            maxLength: MAX_INTRO_LENGTH,
          }}
          multiline
          rows={4}
        />
      </div>

      <div className="flex items-stretch space-x-0 flex-wrap md:flex-nowrap md:space-x-5 mt-5">
        <Controller
          name={toFormName('coverImage')}
          control={control}
          render={({
            field: { value, onChange, ref },
            fieldState: { error },
          }) => (
            <LandingMediaDropbox
              upload={p.imageUpload}
              inputRef={ref}
              label="Cover image"
              validationText="The Intro image is used as the preview for the intro video. It will be cropped as square."
              value={value}
              onChange={onChange}
              type="image"
              icon={faImage}
              errorMsg={error?.message}
            >
              <ul className="text-black list-none text-center text-h12">
                <li>Square image/gif, max 5MB</li>
              </ul>
            </LandingMediaDropbox>
          )}
        />{' '}
        <Controller
          name={toFormName('introVideo')}
          control={control}
          render={({
            field: { value, onChange, ref },
            fieldState: { error },
          }) => (
            <LandingMediaDropbox
              upload={p.videoUpload}
              inputRef={ref}
              label="Intro video"
              validationText="The intro video is used to provide a compelling introduction to the creator’s app and channel."
              value={value}
              onChange={onChange}
              type="video"
              icon={faVideo}
              errorMsg={error?.message}
              optional
            >
              <ul className="text-black list-none text-center text-h12">
                <li>Max 90 seconds</li>
                <li>Max 100MB</li>
              </ul>
            </LandingMediaDropbox>
          )}
        />
      </div>
    </div>
  );

  const secondCol = (
    <div className="w-100 ml-0 mt-5 lg:ml-[60px] lg:mt-0 flex-grow">
      <Controller
        name={toFormName('logo')}
        control={control}
        render={({
          field: { value, onChange, ref },
          fieldState: { error },
        }) => (
          <LandingMediaDropbox
            optional
            upload={p.imageUpload}
            inputRef={ref}
            label="Logo"
            validationText={
              <span>
                Upload your unique logo that represents you.
                <br />- PNG Format, transparent background
              </span>
            }
            value={value}
            onChange={onChange}
            type="image"
            icon={faImage}
            errorMsg={error?.message}
            short
          />
        )}
      />
      <div className="mt-8">
        <Controller
          name={toFormName('primaryColor')}
          control={control}
          render={({
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            field: { value, onChange, ref },
            fieldState: { error },
          }) => (
            <ColorPicker
              value={value}
              onChange={onChange}
              error={error?.message}
            />
          )}
        />
      </div>
      <CTAFormComponent index={p.index} />
    </div>
  );

  return <TwoColumns first={firstCol} second={secondCol} />;
}
